import React, { useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import Chip from '@material-ui/core/Chip'
import ActionsMenu from 'components/actions-menu'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import { DateTime } from 'luxon'
import { useIsTablet } from 'lib/hooks'
import SyncAltOutlinedIcon from 'components/material-symbol/icons/SyncAlt'
import CancelOutlinedIcon from 'components/material-symbol/icons/Cancel'
import RemoveSponsorshipModal from './remove-sponsorship-modal'
import { CreateEndorsementInvitation } from './queries'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { getInitials } from 'lib/text-display'

const STATUS_CHIP_STYLES = {
  active: {
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    label: 'Accepted',
  },
  pending: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
    label: 'Invited',
  },
  expired: {
    backgroundColor: '#E8E8E8',
    color: '#111111',
    label: 'Expired',
  },
}

const useStyles = makeStyles(theme => ({
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  sponsorshipAvatar: {
    height: '36px',
    width: '36px',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 400,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  plainActionButton: {
    padding: 0,
    minWidth: 0,
    width: 24,
    height: 24,
    color: '#6B7C87',
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#172024',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  statusChip: {
    borderRadius: 8,
    height: 20,
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
    '& .MuiChip-label': {
      color: 'inherit',
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const SponsorshipItemView = ({ endorsement, refetch }) => {
  const { name, status, title, expiredAt, facility, id, email } = endorsement
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const isTablet = useIsTablet()
  const [removeSponsorshipOpen, setRemoveSponsorshipOpen] = useState(false)
  const [CreateEndorsement] = useMutation(CreateEndorsementInvitation)

  const handleRemoveSponsorship = event => {
    event.stopPropagation()
    setRemoveSponsorshipOpen(true)
  }

  const handleResendSponsorship = async event => {
    event.stopPropagation()
    try {
      const response = await CreateEndorsement({
        variables: {
          input: {
            firstName: name.split(' ')[0],
            lastName: name.split(' ')[1],
            email: email,
            title: title,
            facilityId: facility.id,
          },
        },
      })
      if (response?.data?.createEndorsementInvitation.success) {
        enqueueSnackbar('Endorsement Resent', {
          variant: 'success',
        })
        refetch()
      }
    } catch (e) {
      enqueueSnackbar('Sorry, an error occurred', {
        variant: 'error',
      })
    }
  }

  const statusesMapper = {
    active: STATUS_CHIP_STYLES.active,
    expired: STATUS_CHIP_STYLES.expired,
    pending: STATUS_CHIP_STYLES.pending,
  }

  const statusChip = statusesMapper[status] || STATUS_CHIP_STYLES.pending

  const expirationLabel = expiredAt
    ? DateTime.fromISO(expiredAt).toFormat('MMM dd, yyyy')
    : '—'

  return (
    <>
      <TableRow>
        <TableCell className={classes.info}>
          <Box className={classes.nameCell}>
            <Avatar className={classes.sponsorshipAvatar}>
              {getInitials(name)}
            </Avatar>
            <Box className={classes.info}>
              <Typography className={classes.text} noWrap>
                {name}
              </Typography>
              {title && (
                <Typography className={classes.textSecondary} noWrap>
                  {title}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        {!isTablet && (
          <>
            <TableCell>
              <Typography className={classes.text}>
                {expirationLabel}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.text} noWrap>
                {facility.name}
              </Typography>
            </TableCell>
          </>
        )}
        <TableCell>
          <Chip
            label={statusChip.label}
            size="small"
            className={classes.statusChip}
            style={{
              backgroundColor: statusChip.backgroundColor,
              color: statusChip.color,
            }}
          />
        </TableCell>
        <TableCell
          className={classes.actions}
          onClick={event => event.stopPropagation()}
        >
          <ActionsMenu
            ariaId={`sponsorship-actions-${id}`}
            aria-label="Sponsorship actions"
            buttonContent={<MoreHoriz fontSize="small" />}
            className={classes.plainActionButton}
            color="default"
          >
            {status !== 'active' && (
              <MenuItem onClick={handleResendSponsorship}>
                <ListItemIcon className={classes.menuIcon}>
                  <SyncAltOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Resend Sponsorship" />
              </MenuItem>
            )}
            {status !== 'expired' && (
              <MenuItem onClick={handleRemoveSponsorship}>
                <ListItemIcon className={classes.menuIcon}>
                  <CancelOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Remove Access" />
              </MenuItem>
            )}
          </ActionsMenu>
        </TableCell>
      </TableRow>
      <RemoveSponsorshipModal
        open={removeSponsorshipOpen}
        endorsementId={id}
        onClose={() => setRemoveSponsorshipOpen(false)}
        refetch={refetch}
      />
    </>
  )
}

export default SponsorshipItemView
